import { FlexBox, Skeleton, Text } from '@dixa/lib-shared-habla';
import { colors, typography } from '@dixa/lib-shared-tokens';
import { formatInstrumentNumber, MetricInstrumentData } from 'lib-analytics-ui';
import { DailyConversationsMetric } from 'lib-dashboard-types';
import { SearchFilters } from 'lib-search-types';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { memo, useMemo } from 'react';

import { QueueDashboardMetric } from 'app/views/layout/views/dashboard/views/queue/components/queues/components/metric';
import { createSearchUrlParams } from 'app/views/layout/views/search/utils';
import { AppLink } from 'components/appLink';
import { useQueueDashboardQueueContext } from '../../../queue/queueDashboardQueue.context';

import styles from './dailyConversationsMetric.module.scss';

type QueueDashboardDailyConversationsMetricProps = {
  metric?: DailyConversationsMetric;
} & PropsWithClassName;

export const QueueDashboardDailyConversationsMetric = memo(
  function QueueDashboardDailyConversationsMetric({
    className,
    metric,
  }: QueueDashboardDailyConversationsMetricProps): JSX.Element {
    const { queue, allQueueIds, isSkeleton } = useQueueDashboardQueueContext();

    const {
      average_assignment_time = null,
      total_answered = 0,
      total_received = 0,
      total_abandoned = 0,
    } = metric ?? {};

    const noDataColor =
      !total_answered && !total_received
        ? typography.color.tertiary
        : undefined;

    const abandonedSearchUrlParams = useMemo(() => {
      const filters: SearchFilters<'conversations'> = {
        strategy: 'all',
        filters: [
          {
            field: 'abandoned_at',
            operator: 'between',
            values: [{ value: 'today' }],
          },
        ],
      };

      if (queue || allQueueIds?.length) {
        filters.filters.push({
          field: 'queue_id',
          operator: 'is_one',
          values: queue
            ? [{ value: queue.id }]
            : allQueueIds!.map((id) => ({
                value: id,
              })),
        });
      }

      return createSearchUrlParams({
        scope: 'conversations',
        filters,
      });
    }, [queue, allQueueIds]);

    return (
      <QueueDashboardMetric
        className={className}
        metricType="daily_conversations"
        isSkeleton={isSkeleton}
      >
        <FlexBox flexDirection="column" gap="250">
          {isSkeleton ? (
            <>
              <Skeleton height="24px" width="72px" />
              <Skeleton height="16px" width="144px" />
            </>
          ) : (
            <>
              <div>
                <MetricInstrumentData
                  data={total_answered}
                  color={noDataColor ?? colors.green[500]}
                  format="number"
                />

                <Text.Heading
                  variant="heading-1"
                  as="span"
                  fontSize="600"
                  padding="0 200"
                  color={noDataColor}
                >
                  /
                </Text.Heading>

                <MetricInstrumentData
                  data={total_received}
                  format="number"
                  color={noDataColor}
                />
              </div>

              <FlexBox flexDirection="column" gap="100">
                {total_abandoned ? (
                  <Text.Paragraph variant="4" as="div" subdued>
                    <AppLink
                      path="/search/:scope"
                      params={abandonedSearchUrlParams}
                      className={styles.link}
                    >
                      Abandoned:{' '}
                    </AppLink>

                    <Text.Paragraph
                      variant="3"
                      as="span"
                      color="red.600"
                      className={styles.nowrap}
                    >
                      {formatInstrumentNumber(total_abandoned, 'number')}
                    </Text.Paragraph>
                  </Text.Paragraph>
                ) : null}

                <Text.Paragraph variant="4" as="div" subdued>
                  Avg. assignment time:{' '}
                  <Text.Paragraph
                    variant="3"
                    as="span"
                    color={
                      !average_assignment_time
                        ? typography.color.tertiary
                        : undefined
                    }
                    className={styles.nowrap}
                  >
                    {average_assignment_time
                      ? formatInstrumentNumber(
                          average_assignment_time,
                          'duration',
                        )
                      : 'n.a.'}
                  </Text.Paragraph>
                </Text.Paragraph>
              </FlexBox>
            </>
          )}
        </FlexBox>
      </QueueDashboardMetric>
    );
  },
);
